import { OverviewData } from '../hooks/useOverviewData';

export type InsightSeverity = 'positive' | 'warning' | 'critical';

export interface Insight {
    id: string;
    severity: InsightSeverity;
    /**
     * i18n key + default for the headline. Values are interpolated into the
     * ICU message at render time.
     */
    titleId: string;
    titleDefault: string;
    titleValues?: Record<string, React.ReactNode | string | number>;
    /** Optional second line for context — used by concentration warning etc. */
    subId?: string;
    subDefault?: string;
    subValues?: Record<string, React.ReactNode | string | number>;
    /** Higher rank surfaces first. */
    rank: number;
}

// Threshold below which a pulse delta isn't interesting enough to surface.
const PULSE_THRESHOLD_PCT = 15;
const PULSE_CRITICAL_PCT = 30;
const CONCENTRATION_WARNING_PCT = 30;
const CONCENTRATION_CRITICAL_PCT = 50;

/**
 * Surface 3 things worth knowing on top of the dashboard:
 *   1) Pulse metrics with material movement (|delta| >= 15%)
 *   2) The stage primarily driving rework (when rework is up)
 *   3) Client revenue concentration risk
 *
 * Backend pre-inverts the sign for "lower is better" metrics, so positive
 * delta always = improvement. Severity follows directly.
 */
export function computeInsights(data: OverviewData | null): Insight[] {
    if (!data) return [];
    const insights: Insight[] = [];

    // ── 1. Pulse anomalies ───────────────────────────────────────────────
    const pulseChecks: Array<{ key: string; delta: number | null; labelKey: string; labelDefault: string }> = [
        {
            key: 'revenue',
            delta: data.pulse.revenue.delta_pct,
            labelKey: 'analyticsV2.kpi.revenue',
            labelDefault: 'Revenue',
        },
        {
            key: 'completions',
            delta: data.pulse.completions_count.delta_pct,
            labelKey: 'analyticsV2.kpi.completions',
            labelDefault: 'Completions',
        },
        {
            key: 'cycle_time',
            delta: data.pulse.avg_cycle_time_seconds.delta_pct,
            labelKey: 'analyticsV2.kpi.cycleTime',
            labelDefault: 'Cycle time',
        },
        {
            key: 'rework_rate',
            delta: data.pulse.rework_rate_pct.delta_pct,
            labelKey: 'analyticsV2.kpi.reworkRate',
            labelDefault: 'Rework rate',
        },
    ];

    for (const check of pulseChecks) {
        if (check.delta == null) continue;
        const abs = Math.abs(check.delta);
        if (abs < PULSE_THRESHOLD_PCT) continue;
        const positive = check.delta > 0;
        const severity: InsightSeverity = positive
            ? 'positive'
            : abs >= PULSE_CRITICAL_PCT
            ? 'critical'
            : 'warning';
        insights.push({
            id: `pulse-${check.key}`,
            severity,
            titleId: positive ? 'analyticsV2.insight.improved' : 'analyticsV2.insight.worsened',
            titleDefault: positive ? '{metric} improved {pct}%' : '{metric} worsened {pct}%',
            titleValues: {
                metric: { __i18n: { id: check.labelKey, default: check.labelDefault } },
                pct: abs.toFixed(1),
            },
            rank: abs,
        });
    }

    // ── 2. Rework driver attribution ─────────────────────────────────────
    const reworkDelta = data.pulse.rework_rate_pct.delta_pct;
    if (
        reworkDelta != null &&
        reworkDelta <= -10 && // backend pre-inverts; negative = worse rework
        data.rework_leaderboard.length > 0 &&
        data.rework_leaderboard_previous
    ) {
        const prevMap = new Map(
            data.rework_leaderboard_previous.map((r) => [r.responsibility_id, r.total_reworks]),
        );
        let topGainer: { name: string; gain: number } | null = null;
        for (const stage of data.rework_leaderboard) {
            const prev = prevMap.get(stage.responsibility_id) ?? 0;
            const gain = stage.total_reworks - prev;
            if (gain > 0 && (!topGainer || gain > topGainer.gain)) {
                topGainer = { name: stage.name, gain };
            }
        }
        if (topGainer) {
            insights.push({
                id: 'rework-driver',
                severity: 'warning',
                titleId: 'analyticsV2.insight.reworkDriver',
                titleDefault: 'Rework concentrated in {stage} (+{count} this period)',
                titleValues: { stage: topGainer.name, count: topGainer.gain },
                rank: 90, // ranks just below extreme pulse moves
            });
        }
    }

    // ── 3. Client concentration ──────────────────────────────────────────
    if (data.top_clients.length > 0) {
        const top = data.top_clients[0];
        // True concentration = the top client's share of ALL revenue. Using
        // the sum of top_clients (a backend-capped top-10 leaderboard) as the
        // denominator overstates concentration and triggers false "critical"
        // alarms, so divide into total revenue (pulse.revenue.current); fall
        // back to the top-10 sum only when total revenue is unavailable.
        const totalRevenue = data.pulse.revenue.current;
        const denom =
            totalRevenue != null && totalRevenue > 0 && totalRevenue >= top.revenue
                ? totalRevenue
                : data.top_clients.reduce((s, c) => s + c.revenue, 0);
        const share = denom > 0 ? Math.min(100, (top.revenue / denom) * 100) : 0;
        if (share >= CONCENTRATION_WARNING_PCT) {
            const severity: InsightSeverity =
                share >= CONCENTRATION_CRITICAL_PCT ? 'critical' : 'warning';
            insights.push({
                id: 'client-concentration',
                severity,
                titleId: 'analyticsV2.insight.clientConcentration',
                titleDefault: '{client} drives {pct}% of revenue',
                titleValues: { client: top.name, pct: share.toFixed(0) },
                subId: 'analyticsV2.insight.concentrationHint',
                subDefault: 'High concentration — diversifying lowers risk',
                rank: share,
            });
        }
    }

    return insights.sort((a, b) => b.rank - a.rank).slice(0, 3);
}
